import queryString from 'query-string';

export class AppRoute<
  TGroupPath extends string,
  TGroupParam extends string,
  TRelativePath extends string,
  TRelativeParam extends string,
  TSearchParams extends string,
> {
  public readonly groupPath: TGroupPath;

  public readonly groupParams: TGroupParam[];

  public readonly relativePath: TRelativePath;

  public readonly relativeParams: TRelativeParam[] | never;

  public readonly searchParams: TSearchParams[] | never;

  constructor(data: {
    groupPath: TGroupPath;
    groupParams?: TGroupParam[];
    relativePath: TRelativePath;
    relativeParams?: TRelativeParam[] | never;
    searchParams?: TSearchParams[] | never;
  }) {
    this.groupPath = data.groupPath;
    this.groupParams = data.groupParams || [];

    this.relativePath = data.relativePath;
    this.relativeParams = data.relativeParams || [];

    this.searchParams = data.searchParams || [];
  }

  get fullPath(): `${TGroupPath}/${TRelativePath}` {
    return `${this.groupPath}/${this.relativePath}`;
  }

  /**
   * Full path with params.
   *
   * The given params & searchParams are set to the url accordingly
   */
  with(
    params: Record<TGroupParam | TRelativeParam, string | number> &
      Partial<Record<TSearchParams, string | number>>,
  ) {
    const paramKeys = Object.keys(params);

    // Url

    const pathnameKeys = paramKeys.filter(
      (paramKey) =>
        (this.groupParams as string[]).includes(paramKey) ||
        (this.relativeParams as string[]).includes(paramKey),
    ) as (TGroupParam | TRelativeParam)[];

    const pathname = pathnameKeys.reduce((currentPath: string, param) => {
      const value = params[param].toString();
      return currentPath.replace(`:${param}`, value);
    }, this.fullPath);

    // Search

    const searchKeys = Object.keys(params).filter((paramKey) =>
      (this.searchParams as string[]).includes(paramKey),
    ) as TSearchParams[];

    const searchParams = searchKeys.reduce(
      (acc, searchKey) => ({ ...acc, [searchKey]: params[searchKey] }),
      {},
    );

    const search = searchKeys.length > 0 && queryString.stringify(searchParams);

    return search ? `${pathname}?${search}` : pathname;
  }
}
